import React from "react"
import {useTranslation} from "react-i18next"
import {ArrowRightIcon} from "@heroicons/react/24/outline"
import {Cell, Pie, PieChart, ResponsiveContainer} from "recharts"
import {twMerge} from "tailwind-merge"

import {
  AKlaraDashboardDealChipValues,
  AKlaraDashboardPipeline,
  AKlaraDashboardSalesPersonRow,
} from "../../../services/types.generated.ts"
import {KlaraFiltersContext, TKlaraChip} from "../filters.tsx"
import {useKlaraSummary} from "../useDashboardClock.ts"
import {useKlaraSalesPeople} from "../useKlaraSalesPeople.ts"
import {CardState} from "./CardState.tsx"
import {CARD_CLASS_NAME, CARD_TITLE_CLASS_NAME} from "./cardStyles.ts"
import {PersonChip} from "./PersonChip.tsx"

const PLANNED_COLOR = "var(--color-cr-cyan)"
const OVERDUE_COLOR = "var(--color-cr-blue-dark)"
const MISSING_COLOR = "var(--color-cr-indigo-soft)"
const WON_COLOR = "var(--color-cr-cyan-deep)"
const LOST_COLOR = "var(--color-cr-blue-mid-light)"
const EMPTY_COLOR = "var(--color-cr-slate-8)"

const DONUT_SIZE = 148
const DONUT_OUTER_RADIUS = DONUT_SIZE / 2
// The ring is 20px thick.
const DONUT_INNER_RADIUS = DONUT_OUTER_RADIUS - 20
const DONUT_MARGIN = {top: 0, right: 0, bottom: 0, left: 0}
// The backend's fixed rotten threshold, for a person's pipeline while the summary is unavailable.
const DEFAULT_ROTTEN_DAYS = 14

const ZERO_PIPELINE: AKlaraDashboardPipeline = {open: 0, planned: 0, overdue: 0, missing: 0, rotten: 0, won: 0, lost: 0}

/** A sales person's pipeline with their period outcomes; all zeros for a person without a row in the period. */
export const personPipeline = (row: AKlaraDashboardSalesPersonRow | undefined): AKlaraDashboardPipeline =>
  row ? {...row.pipeline, won: row.won, lost: row.lost} : ZERO_PIPELINE

/** Rounded share of the open deals; null without open deals. */
export const percentOfOpen = (count: number, open: number): number | null =>
  open === 0 ? null : Math.round((count / open) * 100)

/** The team's pipeline from the summary or, with a sales person selected, the person's from their row. */
const usePipelineSource = (salesPersonExternalId: string | undefined) => {
  const summary = useKlaraSummary()
  const salesPeople = useKlaraSalesPeople()
  const rottenDays = summary.data?.data.rotten_days ?? DEFAULT_ROTTEN_DAYS

  if (!salesPersonExternalId) {
    return {pipeline: summary.data?.data.pipeline, rottenDays, query: summary}
  }

  const row = salesPeople.data?.data.sales_people.find(
    person => person.sales_person.external_id === salesPersonExternalId
  )
  return {pipeline: salesPeople.data && personPipeline(row), rottenDays, query: salesPeople}
}

/** Open deals by state, the period's outcomes and the rotten deals; follows the sales person filter. */
export const PipelineCard: React.FC = () => {
  const {t} = useTranslation()
  const titleId = React.useId()
  const {salesPersonExternalId, chip, setChip} = KlaraFiltersContext.useContext()
  const {pipeline, rottenDays, query} = usePipelineSource(salesPersonExternalId)

  return (
    <section
      aria-labelledby={titleId}
      className={twMerge(CARD_CLASS_NAME, "flex min-w-0 flex-[1_1_340px] flex-col px-6 py-5")}
    >
      <div className={"flex flex-wrap items-baseline gap-x-2.5 gap-y-1"}>
        <h2 id={titleId} className={CARD_TITLE_CLASS_NAME}>
          {t("KlaraDashboard_Pipeline_Title")}
        </h2>
        {pipeline && (
          <span className={"text-xs whitespace-nowrap text-cr-slate-60"}>
            {t("KlaraDashboard_Pipeline_Subtitle", {count: pipeline.open})}
          </span>
        )}
        <span className={"self-center"}>
          <PersonChip />
        </span>
      </div>
      <CardState query={query} skeletonClassName={"mt-4 h-64"} fill>
        {pipeline && (
          <div className={"flex grow flex-col"}>
            <div className={"mt-[18px] flex flex-1 flex-wrap items-center gap-6"}>
              <PipelineDonut pipeline={pipeline} />
              <ul className={"flex min-w-0 flex-[1_1_160px] flex-col gap-0.5"}>
                <LegendItem
                  chip={AKlaraDashboardDealChipValues.Planned}
                  color={PLANNED_COLOR}
                  label={t("KlaraDashboard_Pipeline_Planned")}
                  count={pipeline.planned}
                  open={pipeline.open}
                />
                <LegendItem
                  chip={AKlaraDashboardDealChipValues.Overdue}
                  color={OVERDUE_COLOR}
                  label={t("KlaraDashboard_Pipeline_Overdue")}
                  count={pipeline.overdue}
                  open={pipeline.open}
                />
                <LegendItem
                  chip={AKlaraDashboardDealChipValues.Missing}
                  color={MISSING_COLOR}
                  label={t("KlaraDashboard_Pipeline_Missing")}
                  count={pipeline.missing}
                  open={pipeline.open}
                />
                <li aria-hidden={"true"} className={"mx-2 my-1 border-t border-cr-slate-6"} />
                <LegendItem
                  chip={AKlaraDashboardDealChipValues.Won}
                  color={WON_COLOR}
                  label={t("KlaraDashboard_Pipeline_Won")}
                  count={pipeline.won}
                />
                <LegendItem
                  chip={AKlaraDashboardDealChipValues.Lost}
                  color={LOST_COLOR}
                  label={t("KlaraDashboard_Pipeline_Lost")}
                  count={pipeline.lost}
                />
              </ul>
            </div>
            <button
              type={"button"}
              data-testid={"rotten callout"}
              onClick={() => setChip(AKlaraDashboardDealChipValues.Rotten)}
              className={twMerge(
                "mt-4 flex w-full items-center gap-2.5 rounded-[9px] border border-cr-blue-mid-light px-3 py-2.5 text-left transition-colors hover:border-cr-blue-mid",
                chip === AKlaraDashboardDealChipValues.Rotten ? "bg-cr-blue-light" : "bg-cr-blue-super-light"
              )}
            >
              <span
                aria-hidden={"true"}
                className={
                  "size-7 shrink-0 rounded-[7px] bg-[repeating-linear-gradient(135deg,var(--color-cr-blue)_0_3px,var(--color-cr-indigo-soft)_3px_6px)]"
                }
              />
              <span className={"min-w-0 flex-1"}>
                <span className={"block text-[13px] text-cr-slate-90"}>
                  <b className={"font-bold text-cr-blue"}>
                    {t("KlaraDashboard_Pipeline_Rotten", {count: pipeline.rotten})}
                  </b>{" "}
                  {t("KlaraDashboard_Pipeline_RottenOf", {count: pipeline.open})}
                </span>
                <span className={"sr-only"}> · </span>
                <span className={"block text-[11.5px] text-cr-slate-60"}>
                  {t("KlaraDashboard_Pipeline_RottenDays", {days: rottenDays})}
                </span>
              </span>
              <span className={"sr-only"}> · </span>
              <span className={"flex shrink-0 items-center gap-1 text-xs font-semibold whitespace-nowrap text-cr-blue"}>
                {t("KlaraDashboard_Pipeline_View")}
                <ArrowRightIcon className={"size-3.5"} aria-hidden={"true"} />
              </span>
            </button>
          </div>
        )}
      </CardState>
    </section>
  )
}

const PipelineDonut: React.FC<{pipeline: AKlaraDashboardPipeline}> = ({pipeline}) => {
  const {t} = useTranslation()
  const isEmpty = pipeline.open === 0
  const slices = isEmpty
    ? [{name: "empty", value: 1, color: EMPTY_COLOR}]
    : [
        {name: "planned", value: pipeline.planned, color: PLANNED_COLOR},
        {name: "overdue", value: pipeline.overdue, color: OVERDUE_COLOR},
        {name: "missing", value: pipeline.missing, color: MISSING_COLOR},
      ]

  return (
    <div className={"relative shrink-0"} style={{width: DONUT_SIZE, height: DONUT_SIZE}}>
      <ResponsiveContainer width={"100%"} height={"100%"}>
        <PieChart aria-hidden={"true"} margin={DONUT_MARGIN}>
          <Pie
            data={slices}
            dataKey={"value"}
            nameKey={"name"}
            innerRadius={DONUT_INNER_RADIUS}
            outerRadius={DONUT_OUTER_RADIUS}
            startAngle={90}
            endAngle={-270}
            stroke={"none"}
            isAnimationActive={false}
          >
            {slices.map(slice => (
              <Cell key={slice.name} fill={slice.color} />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
      <div
        data-testid={"pipeline centre"}
        className={"absolute inset-0 flex flex-col items-center justify-center text-center"}
      >
        {isEmpty ? (
          <span className={"px-6 text-xs text-cr-slate-60"}>{t("KlaraDashboard_Pipeline_Empty")}</span>
        ) : (
          <>
            <span className={"text-[30px] leading-none font-extrabold tracking-[-1px] text-cr-blue-dark tabular-nums"}>
              {pipeline.open.toLocaleString()}
            </span>
            <span className={"mt-[3px] text-[11.5px] text-cr-slate-60"}>
              {t("KlaraDashboard_Pipeline_OpenDeals", {count: pipeline.open})}
            </span>
          </>
        )}
      </div>
    </div>
  )
}

const LegendItem: React.FC<{
  chip: TKlaraChip
  label: string
  count: number
  /** Open deals to show the count's share of; outcomes show the count only. */
  open?: number
  color: string
}> = ({chip, label, count, open, color}) => {
  const {t} = useTranslation()
  const {chip: activeChip, setChip} = KlaraFiltersContext.useContext()
  const percent = open === undefined ? null : percentOfOpen(count, open)
  const isActive = activeChip === chip

  return (
    <li>
      <button
        type={"button"}
        data-testid={`pipeline ${chip}`}
        aria-pressed={isActive}
        onClick={() => setChip(chip)}
        className={twMerge(
          "grid w-full grid-cols-[10px_minmax(0,1fr)_auto_40px] items-center gap-2.5 rounded-[7px] px-2 py-[7px] text-left transition-[background-color,opacity] hover:bg-cr-slate-1",
          isActive && "bg-cr-blue-light hover:bg-cr-blue-light",
          activeChip !== null && !isActive && "opacity-45"
        )}
      >
        <span aria-hidden={"true"} className={"size-2.5 rounded-[3px]"} style={{backgroundColor: color}} />
        <span className={"truncate text-[13px] text-cr-grey-80"}>{label}</span>
        <span className={"text-[13px] font-bold text-cr-slate-90 tabular-nums"}>{count.toLocaleString()}</span>
        <span className={"text-right text-[11.5px] text-cr-slate-40 tabular-nums"}>
          {percent !== null && t("KlaraDashboard_Pipeline_Percent", {percent})}
        </span>
      </button>
    </li>
  )
}
